import React, { useEffect, useRef } from "react";
import Plyr from "plyr";
import "plyr/dist/plyr.css";
import plyrSpriteUrl from "@/assets/plyr.svg?url";

import { getVideoUrl } from "@/utils/getVideoUrl";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary/ErrorBoundary";

export interface VideoPlayerProps {
  /** Filename (ex: "abc123.mp4") or full http(s) URL. Resolved internally. */
  src?: string;
  className?: string;
  preload?: "none" | "metadata" | "auto";
  /** Reserved for future chapter navigation. Not used in v1. */
  chapters?: Array<{ start: number; end?: number; label: string }>;
  onError?: (error: Event) => void;
}

/** Same-origin assets — avoid cdn.plyr.io (icons/blank fail when CDN is down). */
const plyrOptions: Plyr.Options = {
  controls: [
    "play",
    "progress",
    "current-time",
    "duration",
    "mute",
    "volume",
    "settings",
    "fullscreen",
  ],
  iconUrl: plyrSpriteUrl,
  blankVideo: "/media/plyr-blank.mp4",
  seekTime: 10,
  tooltips: { seek: true },
  ratio: "16:9",
  clickToPlay: true,
  hideControls: true,
  keyboard: { global: false },
  storage: { enabled: false },
};

/**
 * Video player backed by Plyr, driven imperatively.
 *
 * Why imperative (not `plyr-react`): Plyr mutates the DOM around the media
 * element (wraps it, injects controls). With `plyr-react` + a `key={src}`,
 * changing `src` unmounts the subtree while Plyr's DOM is still present, so
 * React's `removeChild` runs on nodes Plyr already moved/removed →
 * "Failed to execute 'removeChild'" → uncaught → white screen.
 *
 * Here React only owns the `<video>` element. Plyr is created in an effect and
 * destroyed in its cleanup (before React removes the node), and `src` changes
 * are applied in-place on the media element (no remount), so React never
 * reconciles Plyr's injected DOM. The ErrorBoundary is kept as defense in
 * depth in case Plyr itself throws during init/destroy.
 */
export const VideoPlayer: React.FC<VideoPlayerProps> = ({
  src,
  className,
  preload = "metadata",
  // Reserved for v2 — accepted in the public API but not wired in v1
  chapters: _chapters,
  onError,
}) => {
  void _chapters;

  const videoRef = useRef<HTMLVideoElement>(null);
  const plyrRef = useRef<Plyr | null>(null);
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const resolvedSrc = getVideoUrl(src);

  // Create Plyr once on mount; destroy on unmount (before React removes the node).
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    const player = new Plyr(el, plyrOptions);
    plyrRef.current = player;

    return () => {
      try {
        player.destroy();
      } catch (err) {
        console.error("VideoPlayer: erro ao destruir Plyr.", err);
      }
      plyrRef.current = null;
    };
  }, []);

  // Apply src changes in-place — no remount, no React reconciliation of Plyr DOM.
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;

    if (resolvedSrc) {
      el.src = resolvedSrc;
    } else {
      el.removeAttribute("src");
    }
    try {
      el.load();
    } catch {
      /* no-op: load() can throw if called during unmount */
    }
  }, [resolvedSrc]);

  if (!resolvedSrc) {
    return (
      <div
        className={`aspect-video bg-gray-200 rounded flex items-center justify-center ${className || ""}`}
      >
        <span className="text-gray-500">Nenhum vídeo selecionado</span>
      </div>
    );
  }

  const nativeFallback = (error: Error) => {
    console.error("VideoPlayer: Plyr falhou, usando player nativo.", error);
    return (
      <video
        src={resolvedSrc}
        controls
        preload={preload}
        className="h-full w-full object-contain"
      />
    );
  };

  return (
    <div className={`aspect-video bg-black rounded overflow-hidden ${className || ""}`}>
      <ErrorBoundary fallback={nativeFallback} resetKey={resolvedSrc}>
        <video
          ref={videoRef}
          className="plyr-video h-full w-full"
          controls
          preload={preload}
          playsInline
        />
      </ErrorBoundary>
    </div>
  );
};

export default VideoPlayer;
